import { useEffect, useState } from "react";
import { toast } from "sonner";
import { format, subMonths } from "date-fns";
import { Plus, Trash } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { DatePicker } from "@/components/DatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = [
  { value: "utilities", label: "Utilities" },
  { value: "staff_wages", label: "Staff wages" },
  { value: "maintenance", label: "Maintenance" },
  { value: "supplies", label: "Supplies" },
  { value: "other", label: "Other" },
];
const CAT_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

const MONTHS = Array.from({ length: 6 }, (_, i) => {
  const d = subMonths(new Date(), i);
  return { value: format(d, "yyyy-MM"), label: format(d, "MMMM yyyy") };
});

export default function ExpensesTab() {
  const [month, setMonth] = useState(MONTHS[0].value);
  const [expenses, setExpenses] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ date: format(new Date(), "yyyy-MM-dd"), category: "utilities", amount: "", note: "" });
  const [saving, setSaving] = useState(false);
  const { fmt } = useCurrency();

  const load = () =>
    api.get(`/accounting/expenses?month=${month}`).then(({ data }) => setExpenses(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { setExpenses(null); load(); }, [month]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/accounting/expenses", { ...form, amount: parseFloat(form.amount) });
      toast.success("Expense recorded");
      setOpen(false);
      setForm({ date: format(new Date(), "yyyy-MM-dd"), category: "utilities", amount: "", note: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    try {
      await api.delete(`/accounting/expenses/${id}`);
      toast.success("Expense deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const total = (expenses || []).reduce((s, e) => s + e.amount, 0);

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <div className="flex items-center justify-between">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-48" data-testid="expenses-month-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MONTHS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setOpen(true)} data-testid="add-expense-button" className="transition-colors">
          <Plus size={16} className="mr-2" /> Record expense
        </Button>
      </div>

      <div className="overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-3 font-medium">Date</th>
              <th className="p-3 font-medium">Category</th>
              <th className="p-3 font-medium">Note</th>
              <th className="p-3 font-medium">Recorded by</th>
              <th className="p-3 text-right font-medium">Amount</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {(expenses || []).map((e) => (
              <tr key={e.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`expense-row-${e.id}`}>
                <td className="p-3">{e.date}</td>
                <td className="p-3">
                  <span className="rounded-sm border border-border bg-secondary px-2 py-0.5 text-xs font-medium">{CAT_LABELS[e.category] || e.category}</span>
                </td>
                <td className="p-3 text-muted-foreground">{e.note || "—"}</td>
                <td className="p-3 text-xs text-muted-foreground">{e.created_by}</td>
                <td className="p-3 text-right font-medium" data-testid={`expense-amount-${e.id}`}>{fmt(e.amount)}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`delete-expense-${e.id}`} onClick={() => remove(e.id)}
                    className="transition-colors hover:border-destructive hover:text-destructive">
                    <Trash size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {expenses && expenses.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="expenses-empty">No expenses recorded for this month.</div>
        )}
        {expenses && expenses.length > 0 && (
          <div className="flex justify-between border-t border-border bg-secondary/40 px-4 py-3 text-sm font-semibold" data-testid="expenses-total">
            <span>Total ({MONTHS.find((m) => m.value === month)?.label})</span>
            <span className="font-heading">{fmt(total)}</span>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Record an expense</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <DatePicker testId="expense-form-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Amount (ZMW)</Label>
              <Input type="number" min="0.01" step="0.01" data-testid="expense-form-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Input data-testid="expense-form-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. ZESCO electricity bill" />
            </div>
            <Button type="submit" disabled={saving} data-testid="expense-form-submit" className="w-full transition-colors">
              {saving ? "Saving…" : "Record expense"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
